import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BodyWrap } from '../components/Main';
import {
  Box,
  FilterBar,
  FilterBarTab,
  View,
  OptimisticCheckbox,
} from '../components/ui';
import {
  ModalProvider,
  useModalProvider,
} from '../components/contexts/ModalContext';
import { assertDefined } from '../lib/util';
import CalculationContext from '../components/contexts/CalculationContext';
import type { TimelineFocus } from '../components/core/Timelines';
import VoyagesTable from '../components/VoyagesTable';
import OperationsTimeline from '../components/timeline/OperationsTimeline';
import {
  isTCOrderEntry,
  isSpotEntry,
  isCoaEntry,
} from '../components/core/Core';
import { hasFeature, useCurrentUser } from '../state/zustand';
import { isInitialScenario } from '../components/Scenario';
import { endSpinner, startSpinner } from '../components/core/util';
import { LoadingOverlay } from '../components/LoadingSpinner';
import type { GlobalPageProps } from './_app';
import { useRelatedOrgsQuery } from '../graphql';

const Operations = (props: GlobalPageProps): JSX.Element => {
  const isArchiveViewAvailable = hasFeature('archive_view');

  const { activeModals, openModal, closeModal, closeAllModals, visibleModal } =
    useModalProvider();

  const [currenttimelineFocus, setCurrenttimelineFocus] =
    useState<TimelineFocus | null>(null);

  const {
    selectedScenarioData: {
      timelines,
      tcTimeDataByVessel,
      spotEntries,
      coaEntries,
    },
    scheduleContext,
    vessels,
    useScenariosResult: { selectedScenario, scenarioOrders },
    calculationConfig,
    coas,
    organizationsById,
    showArchived,
    setShowArchived,
    loading,
  } = props.scenarioTimings;

  useEffect(() => {
    if (!loading && !isInitialScenario(selectedScenario)) {
      return;
    }
    startSpinner();
    return endSpinner;
  }, [loading, selectedScenario]);

  const sharedOrderIds = useMemo(() => {
    return [...selectedScenario.schedule, ...coaEntries, ...spotEntries].reduce<
      Set<number>
    >((set, entry) => {
      if (
        (isTCOrderEntry(entry) || isSpotEntry(entry) || isCoaEntry(entry)) &&
        entry.shared
      ) {
        set.add(entry.orderId);
      }

      return set;
    }, new Set());
  }, [selectedScenario.schedule, coaEntries, spotEntries]);

  const handleOrderView = useCallback(
    (orderId: number): void => {
      openModal({ type: 'VIEW_ORDER', props: { orderId } });
    },
    [openModal],
  );

  const handleViewAssignment = useCallback(
    (timelineFocus: TimelineFocus): void => {
      const timeline = timelines.find((t) => t.id === timelineFocus.timelineId);
      assertDefined(timeline, 'timeline not found');

      setCurrenttimelineFocus(timelineFocus);

      openModal({
        type: 'VIEW_SHIPMENT',
        props: {
          vessels,
          scenario: selectedScenario,
          navTarget: timelineFocus?.navTarget,
        },
      });
    },
    [timelines, openModal, vessels, selectedScenario],
  );

  const [selectedView, setSelectedView] = useState<'table' | 'timeline'>(
    'table',
  );

  const { data: relatedOrgsData } = useRelatedOrgsQuery();

  return (
    <BodyWrap>
      <CalculationContext.Provider value={{ calculationConfig }}>
        <ModalProvider
          {...{
            tcTimeDataByVessel,
            relatedOrgsData,
            activeModals,
            openModal,
            closeModal,
            closeAllModals,
            visibleModal,
            eventTimelines: timelines,
            timelineFocus: currenttimelineFocus ?? undefined,
            VIEW_SHIPMENT: {
              spotAssignments: spotEntries,
              coaAssignments: coaEntries,
              orders: scenarioOrders,
            },
          }}
        >
          <FilterBar height={48} bg="filterbar.background">
            <View flexDirection="row">
              <FilterBarTab
                isActive={selectedView === 'table'}
                onPress={() => setSelectedView('table')}
              >
                Table
              </FilterBarTab>
              <FilterBarTab
                isActive={selectedView === 'timeline'}
                onPress={() => setSelectedView('timeline')}
              >
                Timeline
              </FilterBarTab>
            </View>
            <Box flexDirection="row">
              {isArchiveViewAvailable && (
                <OptimisticCheckbox
                  checked={showArchived}
                  onChange={() => setShowArchived(!showArchived)}
                  label="Include archived voyages"
                  labelColor="neutral.white_50"
                />
              )}
            </Box>
          </FilterBar>
          {selectedView === 'table' && (
            <VoyagesTable
              timelines={timelines}
              coaContracts={coas}
              organizations={organizationsById}
              scheduleContext={scheduleContext}
              onViewOrder={handleOrderView} // Not used currently but don't see why it couldn't be
              onViewAssignment={handleViewAssignment}
              relatedOrgsData={relatedOrgsData}
            />
          )}
          {selectedView === 'timeline' && (
            <OperationsTimeline
              onViewAssignment={handleViewAssignment}
              timelines={timelines}
              scheduleContext={scheduleContext}
              organizations={organizationsById}
              sharedOrderIds={sharedOrderIds}
            />
          )}
        </ModalProvider>
      </CalculationContext.Provider>
    </BodyWrap>
  );
};

export default function OperationsPage(props: GlobalPageProps): JSX.Element {
  const currentUser = useCurrentUser();

  if (!currentUser) {
    return <LoadingOverlay />;
  }

  return <Operations scenarioTimings={props.scenarioTimings} />;
}
